/**
 * planSiteRederive — the per-site bulk correction planner.
 *
 * Computes, WITHOUT writing anything, what "Re-derive site from grid" would
 * do to every fault on the site. The executor applies the approved plan as
 * ONE undoable batch operation. Safety contracts (c6, locked 2026-09-03,
 * revised per c6 review same day — this file is the part reviewed hardest
 * before the tool ever runs):
 *
 *  1. Values tagged source:'human' are NEVER touched by any tier — typed
 *     or chip-defended values survive every bulk run.
 *  2. ABSENT provenance (all 53,098 pre-deploy values) is protected like
 *     'human' from the SILENT tiers. It may be overwritten only under the
 *     ACCEPT authorization, which the UI gates behind the owner's per-site
 *     sampling verdict + explicit confirmation — the sampling IS the
 *     authorization for treating that site's unmarked population as
 *     old-engine output. CLEARS SHARE THIS GATE (c6): deletion is the most
 *     destructive tier, and on the pre-provenance population nothing
 *     distinguishes a hand-typed value from frozen junk — so no clear
 *     applies unless the accept authorization is on AND the field is ticked.
 *  3. Clearing a stored value in a field the derive does not define is
 *     per-field REVIEWABLE: the plan reports a full value HISTOGRAM per
 *     field (frequency is the decision — "INV ×43" is uniform junk, a
 *     mixed histogram is a hand-typed field); nothing clears unless that
 *     field is explicitly ticked.
 *  4. Every change the plan applies writes source:'derived' provenance, so
 *     later runs distinguish machine values cleanly and get safer over time.
 *  5. GRID-QUALITY HOLD-OUT (c6): a fault whose winning cell carries a known
 *     grid-quality flag — unparseable string label, stray direction cluster
 *     (the same strings the Module Grid panel marks ⚠), ambiguous near-tie,
 *     overlapping cells, rejected hierarchy fields — is EXCLUDED from the
 *     destructive tiers (accepts + DEFINED-field clears) by default, opt-in
 *     to include. Clears in fields the site's schemas define NOWHERE
 *     (`definedFields`) are exempt: they are artifact hygiene, true for
 *     every fault site-wide regardless of cell quality (Evron's
 *     inverter-decade dccb), and holding them would teach include-flag
 *     ticks for reasons the guard was never about.
 *     Under grid-exclusive derivation there is no second opinion, and this
 *     tool is where a bad cell would become permanent. Fills and retags
 *     still apply on flagged faults: both write machine-owned values that
 *     the retag tier silently corrects on the next run after the grid is
 *     fixed — self-healing, unlike an accept/clear which destroys the
 *     stored original.
 *
 * Tiers (what the preview shows, what each may touch):
 *  - fills:    blank field ← derived value        (non-destructive, always on)
 *  - retags:   value tagged 'derived' ≠ fresh derive ← updated silently
 *              (machine correcting machine — the provenance contract's point)
 *  - accepts:  untagged stored value ≠ derive     (OWNER-GATED tier;
 *              quality-flagged faults held out by default)
 *  - clears:   untagged stored value in a field the derive defines nothing
 *              for on this fault                   (OWNER-GATED + PER-FIELD
 *              ticked; quality-flagged faults held out by default)
 *  - humanKept: 'human'-tagged disagreements      (reported, never touched)
 */

import type { Indication } from '../../stores/projectStore';
import type { SiteLayout, TopologyFields } from '../layout/types';
import { deriveTopology } from './deriveTopology';
import { detectDominantDirections } from './moduleGrid';
import { topologyValuesEqual, type TopologyProvenance } from './applyDerivedTopology';
import { TOPOLOGY_LEVELS, extractHierarchyFields } from './suggest';
import { effectiveClassification } from '../layout/classify';

const FIELD_KEYS = TOPOLOGY_LEVELS as readonly string[];
const NON_FAULT_TYPES = new Set(['Photo', 'Video']);

export type GridQualityFlag =
  | 'unparseable-label'
  | 'rejected-fields'
  | 'stray-direction'
  | 'ambiguous'
  | 'overlapping-cells';

export interface ClearTally {
  /** total clearable values in this field */
  count: number;
  /** of those, on quality-flagged faults — held out unless includeFlagged */
  flaggedCount: number;
  /** value → occurrences. The modal shows top-N by count; frequency is what
   *  tells an owner whether a field is uniformly junk or mixed. */
  histogram: Record<string, number>;
  /** how many of these sit on string-mode faults (String OC etc.) — a
   *  string-level fault carries no module, so a module clear here is
   *  expected hygiene, not data loss. Only ever non-zero for 'module'. */
  stringMode: number;
}

export interface SiteRederivePlan {
  /** Fields the SITE's schemas can define at all (any string hierarchy
   *  carries the key; string+module always, from the label/cell). A
   *  clearable in a field OUTSIDE this set is artifact hygiene — true for
   *  every fault site-wide, independent of which cell won — and is NOT a
   *  grid-quality question, so the flag hold-out does not apply to it
   *  (c6: a guard bypassed for good reasons teaches bad bypasses; make
   *  the good reason structural instead). */
  definedFields: string[];
  /** per-indication approved property patches, assembled by tier flags */
  items: Array<{
    id: string;
    fills: Record<string, string>;
    retags: Record<string, string>;
    accepts: Record<string, string>;
    /** fields whose stored value would clear (derive defines nothing) */
    clearable: string[];
    /** grid-quality flags on the winning cell — non-empty holds the
     *  destructive tiers (accepts + clears) out of the default run */
    flags: GridQualityFlag[];
    prov: TopologyProvenance;
  }>;
  /** tier tallies for the preview dialog */
  tally: {
    faults: number;
    fills: number;
    retags: number;
    /** accept-tier overwrites on CLEAN faults */
    accepts: number;
    /** accept-tier overwrites held out by grid-quality flags */
    flaggedAccepts: number;
    humanKept: number;
    offGrid: number;
    agree: number;
    /** per-field clear candidates */
    clears: Record<string, ClearTally>;
  };
}

export function planSiteRederive(
  indications: Indication[],
  layouts: SiteLayout[],
): SiteRederivePlan {
  const items: SiteRederivePlan['items'] = [];
  const clears: SiteRederivePlan['tally']['clears'] = {};
  let fills = 0, retags = 0, accepts = 0, flaggedAccepts = 0,
    humanKept = 0, offGrid = 0, agree = 0, faults = 0;

  // Which fields can this site's schemas define AT ALL? Mirrors the derive
  // (effectiveClassification + extractHierarchyFields); string+module are
  // always definable on a gridded site (label value / cell position).
  const definedFields = new Set<string>(['string', 'module']);
  for (const l of layouts) {
    for (const e of l.textEntities) {
      const cls = effectiveClassification(e);
      if (!cls || cls.level !== 'string') continue;
      const h = extractHierarchyFields(cls, l.classificationConfig);
      if (h) for (const k of Object.keys(h)) {
        if (FIELD_KEYS.includes(k)) definedFields.add(k);
      }
    }
  }

  // Stray-direction sets per layout — CLUSTER-SIZE AWARE, not merely
  // non-dominant (c6, 2026-09-03): a string is suspect when it sits in a
  // TINY bearing cluster, not a minority one. Evron's 288-at-255° family
  // is a legitimate second orientation (29% of the site, the owner's
  // freshly corrected strings); Hatzav's 5-at-0° among 644-at-270° are
  // genuine rotation bugs. Calibrated against the 13-site census: every
  // secondary cluster fleet-wide is either a family in the hundreds
  // (Evron 288) or a stray ≤10 (Gadot 1, Neot smadar 2, Hatzav 5, Bnei
  // israel 6/4/3/1, Shoval 10, Sgula 5) — a wide gap; the rule flags a
  // cluster under 5 members OR under 2% of the site's string labels.
  const strayByLayout = new Map<string, Set<string>>();
  for (const l of layouts) {
    const dirs = detectDominantDirections(l);
    if (dirs.length <= 1) continue;
    const total = dirs.reduce((n, d) => n + d.entityCount, 0);
    const s = new Set<string>();
    for (const d of dirs.slice(1)) {
      if (d.entityCount < 5 || d.entityCount / total < 0.02) {
        for (const id of d.entityIds) s.add(id);
      }
    }
    if (s.size > 0) strayByLayout.set(l.id, s);
  }

  for (const ind of indications) {
    if (NON_FAULT_TYPES.has(ind.type)) continue;
    faults++;
    const result = deriveTopology(layouts, ind.geometry, { indicationType: ind.type });
    if (result.kind !== 'grid') { offGrid++; continue; }

    const flags: GridQualityFlag[] = [];
    if (result.unparseableLabel !== undefined) flags.push('unparseable-label');
    if (result.rejectedFields) flags.push('rejected-fields');
    if (strayByLayout.get(result.source.layoutId)?.has(result.cell.entityId)) flags.push('stray-direction');
    if (result.ambiguous) flags.push('ambiguous');
    // Overlapping-cells is a single-module signal only: string-mode markers
    // legitimately cover many cells (same rule as the chip badges).
    if (!result.isString && result.significantCellCount > 2) flags.push('overlapping-cells');

    const props = ind.properties as unknown as Record<string, unknown>;
    const prev = (props.topologyProvenance ?? {}) as Partial<TopologyProvenance>;
    const tag = (k: string) => prev.fields?.[k]; // 'derived' | 'human' | undefined
    const derived = result.fields as Partial<TopologyFields>;

    const item = {
      id: ind.id,
      fills: {} as Record<string, string>,
      retags: {} as Record<string, string>,
      accepts: {} as Record<string, string>,
      clearable: [] as string[],
      flags,
      prov: {
        fields: { ...(prev.fields ?? {}) },
        // rebuilder contract: keptAgainst must survive every machine write
        ...(prev.keptAgainst ? { keptAgainst: prev.keptAgainst } : {}),
      } as TopologyProvenance,
    };
    let anyDisagree = false;

    for (const key of FIELD_KEYS) {
      const d = derived[key as keyof TopologyFields];
      const stored = typeof props[key] === 'string' ? (props[key] as string).trim() : '';
      if (d) {
        if (stored === '') {
          item.fills[key] = d;
          item.prov.fields[key] = 'derived';
          fills++;
        } else if (!topologyValuesEqual(stored, d)) {
          anyDisagree = true;
          if (tag(key) === 'human') {
            humanKept++; // reported, never touched (contract 1)
          } else if (tag(key) === 'derived') {
            item.retags[key] = d; // machine correcting machine (tier 2)
            item.prov.fields[key] = 'derived';
            retags++;
          } else {
            item.accepts[key] = d; // untagged — owner-gated tier (contract 2)
            item.prov.fields[key] = 'derived';
            if (flags.length > 0) flaggedAccepts++; else accepts++;
          }
        }
      } else if (stored !== '' && tag(key) !== 'human') {
        // stored value in a field this fault's derive defines nothing for —
        // clearable ONLY under the accept authorization + per-field tick
        // (contracts 2 + 3)
        item.clearable.push(key);
        const c = (clears[key] ??= { count: 0, flaggedCount: 0, histogram: {}, stringMode: 0 });
        c.count++;
        // flags hold only DEFINED-field clears (a genuine per-cell quality
        // question); a site-undefined field is artifact hygiene everywhere
        if (flags.length > 0 && definedFields.has(key)) c.flaggedCount++;
        c.histogram[stored] = (c.histogram[stored] ?? 0) + 1;
        if (key === 'module' && result.isString) c.stringMode++;
      }
    }

    if (!anyDisagree && Object.keys(item.fills).length === 0 && item.clearable.length === 0) {
      agree++;
      continue;
    }
    item.prov.derived = {
      kind: 'grid',
      layoutId: result.source.layoutId,
      gridSchemaVersion: result.source.gridSchemaVersion,
      configHash: result.source.configHash,
      // flags ride the provenance (c6): after a fill, derived == stored and
      // no chip appears — this keeps "filled from a doubtful grid" findable
      ...(flags.length > 0 ? { flags } : {}),
      at: new Date().toISOString(),
    };
    items.push(item);
  }

  return {
    definedFields: [...definedFields],
    items,
    tally: { faults, fills, retags, accepts, flaggedAccepts, humanKept, offGrid, agree, clears },
  };
}

/**
 * Assemble the final property patches from an approved plan. `acceptTier`
 * is the owner's sampling authorization — it unlocks BOTH destructive
 * tiers (accepts and the ticked clears). `includeFlagged` additionally
 * admits quality-flagged faults into those tiers (default hold-out,
 * contract 5). `clearFields` is the explicit set of per-field ticks.
 */
export function assembleRederivePatches(
  plan: SiteRederivePlan,
  opts: { acceptTier: boolean; includeFlagged: boolean; clearFields: Set<string> },
): Array<{ id: string; patch: Record<string, unknown> }> {
  const out: Array<{ id: string; patch: Record<string, unknown> }> = [];
  const defined = new Set(plan.definedFields);
  for (const item of plan.items) {
    const destructiveOk = opts.acceptTier && (item.flags.length === 0 || opts.includeFlagged);
    const patch: Record<string, unknown> = { ...item.fills, ...item.retags };
    if (destructiveOk) Object.assign(patch, item.accepts);
    const prov: TopologyProvenance = {
      fields: { ...item.prov.fields },
      ...(item.prov.derived ? { derived: item.prov.derived } : {}),
      ...(item.prov.keptAgainst ? { keptAgainst: item.prov.keptAgainst } : {}),
    };
    for (const k of item.clearable) {
      if (!opts.clearFields.has(k) || !opts.acceptTier) continue;
      // the flag hold-out applies only to DEFINED-field clears — a
      // site-undefined field is artifact hygiene, not a grid question
      if (item.flags.length > 0 && defined.has(k) && !opts.includeFlagged) continue;
      patch[k] = '';
      prov.fields[k] = 'derived'; // a machine-made clear is machine-owned
    }
    if (!destructiveOk) {
      // accepts not applied ⇒ their provenance stamps must not land either
      for (const k of Object.keys(item.accepts)) {
        if (!(k in patch)) delete prov.fields[k];
      }
    }
    if (Object.keys(patch).length === 0) continue;
    patch.topologyProvenance = prov;
    out.push({ id: item.id, patch });
  }
  return out;
}
